import React, { useState, useMemo } from "react";

const box = {
  marginTop: "0.9rem",
  padding: "0.9rem 1rem",
  border: "1px solid rgba(79,195,195,0.45)",
  borderRadius: "10px",
  background: "rgba(30,158,158,0.08)",
};

export default function HumanReviewGate({ wf, stepIndex, approval, onApprove }) {
  const [acked, setAcked] = useState({});
  const [docAck, setDocAck] = useState(false);
  const [name, setName] = useState("");

  const findings = useMemo(() => {
    const list = [];
    wf.steps.slice(0, stepIndex + 1).forEach((s) => {
      s.fields.forEach((f) => {
        if (f.c === "warn" || f.c === "err") {
          list.push({ id: `${s.label}-${f.k}`, step: s.label, reg: s.reg, k: f.k, v: f.v });
        }
      });
    });
    return list;
  }, [wf, stepIndex]);

  // 21 CFR §11.50: signature must be the signer's full legal name — initials are rejected.
  // Requires at least two name parts, each with 2+ letters (e.g. "Jane Doe", not "J.D." or "JD").
  const isFullName = /^[A-Za-zÀ-ÿ'-]{2,}(\s+[A-Za-zÀ-ÿ'-]{2,})+$/.test(name.trim());
  const canApprove = docAck && findings.every((f) => acked[f.id]) && isFullName;

  if (approval) {
    return (
      <div style={box}>
        <div style={{ fontWeight: 700, color: "#4FC3C3" }}>✅ Reviewed &amp; Approved</div>
        <p style={{ fontSize: "0.8rem", opacity: 0.8, margin: "0.35rem 0 0" }}>
          E-signed by {approval.name} · {new Date(approval.time).toLocaleString()} — 21 CFR Part 11
          signature applied. Workflow resumed.
        </p>
      </div>
    );
  }

  return (
    <div style={box}>
      <div style={{ fontWeight: 700, marginBottom: "0.35rem" }}>👤 Human Review Required</div>
      <p style={{ fontSize: "0.8rem", opacity: 0.8, margin: "0 0 0.7rem" }}>
        Review the AI-assisted document and each flagged compliance item below. The workflow cannot
        continue until every item is verified and e-signed.
      </p>

      {findings.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.7rem" }}>
          {findings.map((f) => (
            <label
              key={f.id}
              style={{
                display: "flex",
                gap: "0.55rem",
                alignItems: "flex-start",
                fontSize: "0.8rem",
                padding: "0.5rem 0.6rem",
                border: `1px solid ${acked[f.id] ? "rgba(79,195,195,0.5)" : "rgba(245,158,11,0.5)"}`,
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={!!acked[f.id]}
                onChange={(e) => setAcked((a) => ({ ...a, [f.id]: e.target.checked }))}
                style={{ marginTop: "2px" }}
              />
              <span>
                <span style={{ color: "#f59e0b", fontWeight: 600 }}>⚠ {f.k}:</span> {f.v}
                <span style={{ display: "block", opacity: 0.75, marginTop: "2px" }}>
                  Step: {f.step} · Regulation: <span style={{ color: "#4FC3C3" }}>{f.reg}</span>
                </span>
              </span>
            </label>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: "0.8rem", opacity: 0.75, margin: "0 0 0.7rem" }}>
          No non-conformities flagged in this cycle — confirm document accuracy to approve.
        </p>
      )}

      <label style={{ display: "flex", gap: "0.55rem", alignItems: "flex-start", fontSize: "0.8rem", marginBottom: "0.7rem", cursor: "pointer" }}>
        <input type="checkbox" checked={docAck} onChange={(e) => setDocAck(e.target.checked)} style={{ marginTop: "2px" }} />
        <span>
          I have opened and reviewed the AI-assisted document in the preview panel and verified it
          against the applicable regulations ({wf.steps[stepIndex].reg}).
        </span>
      </label>

      {name.trim().length > 0 && !isFullName && (
        <p style={{ fontSize: "0.75rem", color: "#f59e0b", margin: "0 0 0.5rem", fontWeight: 600 }}>
          ⛔ Full legal name required (first and last name) — initials cannot complete this signature (21 CFR §11.50).
        </p>
      )}
      <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          placeholder="Type your full legal name to e-sign (first and last)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{
            flex: "1 1 200px",
            fontSize: "0.8rem",
            padding: "0.45rem 0.6rem",
            borderRadius: "8px",
            border: "1px solid rgba(79,195,195,0.45)",
            background: "transparent",
            color: "inherit",
          }}
        />
        <button
          type="button"
          disabled={!canApprove}
          onClick={() => onApprove(name.trim())}
          style={{
            fontSize: "0.8rem",
            fontWeight: 700,
            padding: "0.5rem 0.9rem",
            borderRadius: "8px",
            border: "none",
            cursor: canApprove ? "pointer" : "not-allowed",
            background: canApprove ? "#1E9E9E" : "rgba(128,128,128,0.35)",
            color: "#fff",
          }}
        >
          ✍️ Approve &amp; E-Sign
        </button>
      </div>
    </div>
  );
}